import { Chip, TextField, Tooltip, Typography } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { Close } from "@mui/icons-material";
import { Autocomplete as MuiAutocomplete } from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { List, Map } from "immutable";
import PropTypes from "prop-types";
import React from "react";

import { processCombo } from "../../core/util";
import { theme } from "../../theme";

function styles() {
  return {
    root: { flexGrow: 1, width: "100%" },
    tag: {
      maxWidth: "calc(100% - 24px) !important",
    },
  };
}

function getLabel(option) {
  if (option == null) return "";
  if (typeof option === "string") return option;
  return option.label || "";
}

/**
 * Wrapper do MUI Autocomplete v9 — versão MINIMAL e DEFENSIVA.
 *
 * Princípios:
 *   - `multiple` segue o prop `multiselect` (NÃO sempre true).
 *   - Em single: `value` é object|null, sem chip, texto no input.
 *   - Em multi: `value` é array, com chips.
 *   - inputValue NÃO é controlado — deixa MUI gerenciar (evita o bug do
 *     issue #32425 onde texto some a cada caractere quando ambos `value`
 *     e `inputValue` são controlados externamente).
 *   - `onInputChange` sintetiza event-like (handlers antigos fazem
 *     `event.target.value` e quebram com event=null nos reasons "reset"/"clear").
 *   - Memoização: options e value derivados são `useMemo` pra estabilidade
 *     referencial (requisito MUI v9 docs pra evitar re-renders descontrolados).
 */
function Autocomplete(props) {
  const {
    id,
    label,
    error,
    style,
    onType,
    onBlur,
    onFocus,
    classes,
    options,
    disabled,
    onChange,
    mainOption,
    helperText,
    multiselect,
    InputProps,
    placeholder,
    InputLabelProps,
    value: rawValue,
  } = props;

  const isMulti = Boolean(multiselect);

  // Garante que cada item tem `.value` e `.label`. O backend retorna itens
  // com `{codigo, nome}` (sem value/label), e os itens salvos do form vêm
  // com `{value, label, codigo, nome}`. Normalizamos pra ambos os casos
  // funcionarem ao editar (ler valor salvo) e ao criar (selecionar novo).
  const normalizeItem = React.useCallback((item) => {
    if (!item || typeof item !== "object") return item;
    const v = item.value != null ? item.value : item.codigo;
    const l = item.label != null ? item.label : (item.nome || item.descricao || "");
    return { ...item, value: v, label: l };
  }, []);

  // Value derivado do prop — sempre array internamente, depois single retira [0].
  const valueArr = React.useMemo(() => {
    const v = rawValue;
    if (!v) return [];
    if (List.isList(v)) {
      return v.size > 0 ? v.toJS().map(normalizeItem) : [];
    }
    if (Map.isMap(v)) {
      return v.size > 0 ? [normalizeItem(v.toJS())] : [];
    }
    if (Array.isArray(v)) return v.map(normalizeItem);
    if (typeof v === "object") return [normalizeItem(v)];
    return [];
  }, [rawValue, normalizeItem]);

  // `multiple` é sempre TRUE — em single mode usamos slice(-1) no onChange
  // pra manter só 1 item. Vantagem: o valor selecionado SEMPRE aparece como
  // chip dentro do campo, com X pra remover. Visualmente uniforme.
  const muiValue = valueArr;

  // Opções: prepend mainOption + GARANTE que o(s) item(ns) selecionado(s) estejam
  // na lista. Sem isso, MUI v9 descarta o value no blur (porque não acha match).
  // mainOption (sem `.value`, ex: "Adicionar Evento" com onClick custom) é
  // preservada SEPARADAMENTE — entra direto no array final, sem dedup.
  const optionsArray = React.useMemo(() => {
    const base = mainOption ? options && options.insert(0, Map(mainOption)) : options;
    const baseArr = base ? base.toJS() : [];
    // Separa items sem `value` (ex: mainOption com `icon`) dos com value.
    const noValueItems = baseArr.filter((o) => o && o.value == null);
    // Items com value: dedup por .value e mescla com valueArr (selecionados).
    const byKey = new window.Map();
    baseArr.forEach((o) => {
      if (o && o.value != null) byKey.set(String(o.value), o);
    });
    valueArr.forEach((v) => {
      if (v && v.value != null && !byKey.has(String(v.value))) {
        byKey.set(String(v.value), v);
      }
    });
    // mainOption (sem value) primeiro, depois items com value.
    return [...noValueItems, ...Array.from(byKey.values())];
  }, [options, mainOption, valueArr]);

  const handleChange = (event, newValue) => {
    let arr = Array.isArray(newValue) ? newValue : (newValue == null ? [] : [newValue]);

    // mainOption ("Adicionar Evento") — roda callback e remove da lista.
    if (mainOption) {
      const main = arr.find((o) => o && o.icon);
      if (main) {
        if (typeof main.onClick === "function") main.onClick();
        arr = arr.filter((o) => o !== main);
      }
    }
    // Single mode: mantém só o último selecionado (substitui em vez de acumular).
    if (!isMulti && arr.length > 1) {
      arr = arr.slice(-1);
    }
    onChange(event, arr);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (typeof onType !== "function") return;
    // Só dispara fetch quando user está DIGITANDO (não em reset/clear/blur).
    if (reason !== "input") return;
    // Handlers antigos fazem `event.target.value` — sintetiza event quando
    // o real é null (algumas chamadas internas do MUI passam null).
    const syntheticEvent =
      event && event.target
        ? event
        : { target: { value: newInputValue || "" } };
    onType(syntheticEvent, newInputValue, reason);
  };

  return (
    <div className={classes.root} style={style}>
      <MuiAutocomplete
        id={id}
        multiple
        disableCloseOnSelect={isMulti}
        classes={{ tag: classes.tag }}
        disableClearable
        // CRÍTICO: sem isso, o MUI descarta o value no blur quando não acha
        // match exato em `options`. Combinado com optionsArray que sempre
        // inclui o value, garante que a seleção persiste após clicar fora.
        clearOnBlur={false}
        selectOnFocus={false}
        getOptionLabel={getLabel}
        options={optionsArray}
        value={muiValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.value == null || val.value == null) return false;
          return String(option.value) === String(val.value);
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        noOptionsText="Sem opções disponíveis"
        // MUI v9: `renderTags` foi REMOVIDO e substituído por `renderValue`.
        // Assinatura: (value, getItemProps, ownerState) => ReactNode SINGLE
        // (Fragment, não array — array de elements no startAdornment não
        // renderiza visualmente em alguns casos).
        renderValue={(tagValue, getItemProps) => (
          <>
            {tagValue.map((option, index) => {
              const itemProps = getItemProps({ index }) || {};
              const { key, onDelete, ...rest } = itemProps;
              return (
                <Tooltip key={key != null ? key : index} title={option.label || ""}>
                  <Chip
                    variant="outlined"
                    label={option.label}
                    {...rest}
                    deleteIcon={<Close />}
                    onDelete={disabled ? undefined : onDelete}
                  />
                </Tooltip>
              );
            })}
          </>
        )}
        renderOption={(liProps, option, { inputValue }) => {
          // `key` separado pra não vazar pelo spread. Resto (handlers de
          // seleção/hover/keyboard) PRECISA ir pro <li> raiz intacto.
          const { key, ...rest } = liProps;
          const labelText = option.label || "";
          const { icon: Icon } = option;

          if (Icon) {
            return (
              <li
                key={key}
                {...rest}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px" }}
              >
                <Icon
                  style={{
                    color: "#fff",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle1">{labelText}</Typography>
              </li>
            );
          }

          const matches = match(labelText, inputValue || "");
          const parts = parse(labelText, matches);
          return (
            <li key={key} {...rest}>
              {parts.map((part, idx) => (
                <span
                  key={`${part.text}-${idx}`}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}
            </li>
          );
        }}
        // MUI v9 (migration guide oficial): `params` agora tem `params.slotProps`
        // (não mais `params.InputProps`). Pattern correto: spread `params` +
        // mergear `slotProps` preservando `...params.slotProps` (que contém
        // ref, startAdornment com chips, endAdornment com dropdown arrow).
        // Tanto resolve o warning "InputProps prop on a DOM element" QUANTO
        // mantém o ref do input funcionando (sem o crash "Unable to find input").
        renderInput={(params) => {
          const acSlot = params.slotProps || {};
          const acInputSlot = acSlot.input || {};
          const userInput = InputProps || {};
          return (
            <TextField
              {...params}
              label={label}
              error={error}
              helperText={helperText}
              placeholder={placeholder}
              slotProps={{
                ...acSlot,
                input: {
                  ...acInputSlot,
                  ...userInput,
                  endAdornment: userInput.endAdornment || acInputSlot.endAdornment,
                },
                inputLabel: {
                  ...(acSlot.inputLabel || {}),
                  ...(InputLabelProps || {}),
                },
              }}
            />
          );
        }}
      />
    </div>
  );
}

Autocomplete.defaultProps = {
  value: null,
  multiselect: false,
  options: List(),
  onType: null,
};

Autocomplete.propTypes = {
  multiselect: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onType: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  options: PropTypes.instanceOf(List),
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default withStyles(styles)(Autocomplete);
